import { useState } from 'react';
import { FileText, Calendar, User, Hash, Package } from 'lucide-react';
import { useI18n } from '@/contexts/i18n-hook';
import { rotuloAreaAtivo } from '@/lib/i18n';
import { ModalSelecionarAtivo } from './modal-selecionar-ativo';

interface Ativo {
  id: string;
  equipamento?: string;
  tag?: string;
  area?: string;
  local?: string;
  tipo?: string;
}

interface CabecalhoIdentificacaoProps {
  dados: {
    numero?: string;
    numeroArcelorMittal?: string;
    revisao?: number;
    numeroEmitente?: string;
    emitente?: string;
    emitirCREA?: string;
    codigoInterno?: string;
    numeroDocumento?: string;
    titulo?: string;
    maquina?: string | { nome?: string };
    ativoId?: string;
    ativo?: Ativo;
    dataEmissao?: string;
    statusLiberacao?: string;
    nomeMaquina?: string;
    tipoMaquina?: string;
    identificacaoMaquina?: string;
    setorMaquina?: string;
  };
  onChange?: (campo: string, valor: any) => void;
  modoEdicao?: boolean;
}

export function CabecalhoIdentificacao({
  dados,
  onChange,
  modoEdicao = false,
}: CabecalhoIdentificacaoProps) {
  const { t, formatarData } = useI18n();
  const [modalAberto, setModalAberto] = useState(false);

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'liberado':
        return 'bg-green-100 text-green-800 border-green-400';
      case 'com_ressalvas':
        return 'bg-yellow-100 text-yellow-800 border-yellow-400';
      case 'nao_liberado':
        return 'bg-red-100 text-red-800 border-red-400';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-400';
    }
  };

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'liberado':
        return t('apreciacaoEdicao.cabecalho.status.liberado');
      case 'com_ressalvas':
        return t('apreciacaoEdicao.cabecalho.status.comRessalvas');
      case 'nao_liberado':
        return t('apreciacaoEdicao.cabecalho.status.naoLiberado');
      default:
        return t('apreciacaoEdicao.cabecalho.status.naoDefinido');
    }
  };

  return (
    <div className="bg-white dark:bg-schmersal-gray-darker shadow rounded-lg p-6 space-y-6 border border-rg-line dark:border-schmersal-gray-dark">
      <div className="flex items-center mb-4">
        <FileText className="h-6 w-6 text-blue-500 dark:text-schmersal-blue mr-2" />
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          {t('apreciacaoEdicao.cabecalho.titulo')}
        </h2>
      </div>

      {/* Título Principal */}
      <div className="border-l-4 border-blue-500 dark:border-schmersal-blue pl-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {dados.titulo || t('apreciacaoEdicao.cabecalho.tituloPadrao')}
        </h3>
      </div>

      {/* Grid de Informações */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* Seleção de Ativo */}
        {modoEdicao && (
          <div className="md:col-span-2 lg:col-span-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              <Package className="h-4 w-4 inline mr-1" />
              {t('apreciacaoEdicao.cabecalho.ativo.label')}
            </label>
            <button
              type="button"
              onClick={() => setModalAberto(true)}
              className="w-full text-left border-2 border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                       focus:ring-2 focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                       bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                       sm:text-sm py-2.5 px-3 hover:border-blue-500 dark:hover:border-schmersal-blue transition-all duration-200"
            >
              {dados.ativo ? (
                <div>
                  <span className="font-semibold">{dados.ativo.equipamento || t('apreciacaoEdicao.cabecalho.ativo.semNome')}</span>
                  {dados.ativo.tag && <span className="text-gray-500 dark:text-gray-400"> - TAG: {dados.ativo.tag}</span>}
                  {dados.ativo.area && <span className="text-gray-500 dark:text-gray-400"> - {rotuloAreaAtivo(t, dados.ativo.area)}</span>}
                  {dados.ativo.local && <span className="text-gray-500 dark:text-gray-400"> / {dados.ativo.local}</span>}
                </div>
              ) : (
                <span className="text-gray-500 dark:text-gray-400">{t('apreciacaoEdicao.cabecalho.ativo.placeholder')}</span>
              )}
            </button>
            {dados.ativo && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange?.('ativo', null);
                }}
                className="mt-2 text-xs text-red-600 dark:text-red-400 hover:underline"
              >
                {t('apreciacaoEdicao.cabecalho.ativo.removerSelecao')}
              </button>
            )}
            <ModalSelecionarAtivo
              aberto={modalAberto}
              ativoSelecionadoId={dados.ativoId}
              onFechar={() => setModalAberto(false)}
              onSelecionar={(ativo) => {
                onChange?.('ativo', ativo);
              }}
            />
          </div>
        )}
        {!modoEdicao && dados.ativo && (
          <div className="md:col-span-2 lg:col-span-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              <Package className="h-4 w-4 inline mr-1" />
              {t('apreciacaoEdicao.cabecalho.ativo.label')}
            </label>
            <p className="text-sm text-gray-900 dark:text-white">
              <span className="font-semibold">{dados.ativo.equipamento || t('apreciacaoEdicao.cabecalho.ativo.naoEspecificado')}</span>
              {dados.ativo.tag && <span className="text-gray-600 dark:text-gray-400"> (TAG: {dados.ativo.tag})</span>}
              {dados.ativo.area && <span className="text-gray-600 dark:text-gray-400"> - {rotuloAreaAtivo(t, dados.ativo.area)}</span>}
              {dados.ativo.local && <span className="text-gray-600 dark:text-gray-400"> / {dados.ativo.local}</span>}
            </p>
          </div>
        )}

        {/* Código Praxis / Número */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
            <Hash className="h-4 w-4 inline mr-1" />
            {t('apreciacaoEdicao.cabecalho.codigoPraxis')}
          </label>
          {modoEdicao ? (
            <input
              type="text"
              value={dados.numero || ''}
              onChange={(e) => onChange?.('numero', e.target.value)}
              className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm
                       focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue
                       bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                       sm:text-sm"
              placeholder={t('apreciacaoEdicao.cabecalho.placeholderCodigoPraxis')}
            />
          ) : (
            <p className="text-sm text-gray-900 dark:text-white">
              {dados.numero || '-'}
            </p>
          )}
        </div>

        {/* Nº ArcelorMittal */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
            <Hash className="h-4 w-4 inline mr-1" />
            {t('apreciacaoEdicao.cabecalho.numeroArcelorMittal')}
          </label>
          {modoEdicao ? (
            <input
              type="text"
              value={dados.numeroArcelorMittal || ''}
              onChange={(e) => onChange?.('numeroArcelorMittal', e.target.value)}
              className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                       focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                       bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                       sm:text-sm"
              placeholder={t('apreciacaoEdicao.cabecalho.placeholderNumeroArcelorMittal')}
            />
          ) : (
            <p className="text-sm text-gray-900 dark:text-white">
              {dados.numeroArcelorMittal || '-'}
            </p>
          )}
        </div>

        {/* Revisão */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
            <FileText className="h-4 w-4 inline mr-1" />
            {t('apreciacaoEdicao.cabecalho.revisao')}
          </label>
          {modoEdicao ? (
            <input
              type="number"
              value={dados.revisao ?? 0}
              onChange={(e) => {
                const valor = e.target.value === '' ? 0 : parseInt(e.target.value);
                onChange?.('revisao', isNaN(valor) ? 0 : valor);
              }}
              className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                       focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                       bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                       sm:text-sm"
              min="0"
            />
          ) : (
            <p className="text-sm text-gray-900 dark:text-white">{dados.revisao ?? 0}</p>
          )}
        </div>

        {/* Nº Emitente */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
            <Hash className="h-4 w-4 inline mr-1" />
            {t('apreciacaoEdicao.cabecalho.numeroEmitente')}
          </label>
          {modoEdicao ? (
            <input
              type="text"
              value={dados.numeroEmitente || ''}
              onChange={(e) => onChange?.('numeroEmitente', e.target.value)}
              className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                       focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                       bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                       sm:text-sm"
              placeholder={t('apreciacaoEdicao.cabecalho.placeholderNumeroEmitente')}
            />
          ) : (
            <p className="text-sm text-gray-900 dark:text-white">
              {dados.numeroEmitente || '-'}
            </p>
          )}
        </div>

        {/* Descrição */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
            {t('comum.rotulo.descricao')}
          </label>
          {modoEdicao ? (
            <textarea
              value={dados.titulo || ''}
              onChange={(e) => onChange?.('titulo', e.target.value)}
              rows={2}
              className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                       focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                       bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                       sm:text-sm"
              placeholder={t('apreciacaoEdicao.cabecalho.placeholderDescricao')}
            />
          ) : (
            <p className="text-sm text-gray-900 dark:text-white">{dados.titulo || '-'}</p>
          )}
        </div>



        {/* Data */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
            <Calendar className="h-4 w-4 inline mr-1" />
            {t('comum.rotulo.data')}
          </label>
          {modoEdicao ? (
            <input
              type="date"
              value={
                dados.dataEmissao
                  ? String(dados.dataEmissao).substring(0, 10)
                  : ''
              }
              onChange={(e) => onChange?.('dataEmissao', e.target.value)}
              className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                       focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                       bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                       sm:text-sm"
            />
          ) : (
            <p className="text-sm text-gray-900 dark:text-white">
              {dados.dataEmissao
                ? formatarData(dados.dataEmissao, { timeZone: 'UTC' })
                : '-'}
            </p>
          )}
        </div>

        {/* Demanda */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
            <Hash className="h-4 w-4 inline mr-1" />
            {t('apreciacaoEdicao.cabecalho.demanda')}
          </label>
          {modoEdicao ? (
            <input
              type="text"
              value={dados.codigoInterno || ''}
              onChange={(e) => onChange?.('codigoInterno', e.target.value)}
              className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                       focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                       bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                       sm:text-sm"
              placeholder={t('apreciacaoEdicao.cabecalho.placeholderDemanda')}
            />
          ) : (
            <p className="text-sm text-gray-900 dark:text-white">
              {dados.codigoInterno || '-'}
            </p>
          )}
        </div>

        {/* Nº Documento (Rodapé) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
            <Hash className="h-4 w-4 inline mr-1" />
            {t('apreciacaoEdicao.cabecalho.numeroDocumento')}
          </label>
          {modoEdicao ? (
            <input
              type="text"
              value={dados.numeroDocumento || ''}
              onChange={(e) => onChange?.('numeroDocumento', e.target.value)}
              className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                       focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                       bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                       sm:text-sm"
              placeholder={t('apreciacaoEdicao.cabecalho.placeholderNumeroDocumento')}
            />
          ) : (
            <p className="text-sm text-gray-900 dark:text-white">
              {dados.numeroDocumento || '-'}
            </p>
          )}
        </div>

        {/* Emitente */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
            <User className="h-4 w-4 inline mr-1" />
            {t('apreciacaoEdicao.cabecalho.emitente')}
          </label>
          {modoEdicao ? (
            <input
              type="text"
              value={dados.emitente || ''}
              onChange={(e) => onChange?.('emitente', e.target.value)}
              className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                       focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                       bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                       sm:text-sm"
              placeholder={t('apreciacaoEdicao.cabecalho.placeholderEmitente')}
            />
          ) : (
            <p className="text-sm text-gray-900 dark:text-white">{dados.emitente || '-'}</p>
          )}
        </div>

        {/* Máquina (Identificação Documental) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
            <Package className="h-4 w-4 inline mr-1" />
            {t('apreciacaoEdicao.cabecalho.maquina')}
          </label>
          {modoEdicao ? (
            <input
              type="text"
              value={dados.nomeMaquina || ''}
              onChange={(e) => onChange?.('nomeMaquina', e.target.value)}
              className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                       focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                       bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                       sm:text-sm"
              placeholder={t('apreciacaoEdicao.cabecalho.placeholderMaquina')}
            />
          ) : (
            <p className="text-sm text-gray-900 dark:text-white">{dados.nomeMaquina || '-'}</p>
          )}
        </div>

        {/* Tipo (Identificação Documental) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
            {t('comum.rotulo.tipo')}
          </label>
          {modoEdicao ? (
            <input
              type="text"
              value={dados.tipoMaquina || ''}
              onChange={(e) => onChange?.('tipoMaquina', e.target.value)}
              className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                       focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                       bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                       sm:text-sm"
              placeholder={t('apreciacaoEdicao.cabecalho.placeholderTipoMaquina')}
            />
          ) : (
            <p className="text-sm text-gray-900 dark:text-white">{dados.tipoMaquina || '-'}</p>
          )}
        </div>

        {/* Identificação (Identificação Documental) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
            <Hash className="h-4 w-4 inline mr-1" />
            {t('apreciacaoEdicao.cabecalho.identificacao')}
          </label>
          {modoEdicao ? (
            <input
              type="text"
              value={dados.identificacaoMaquina || ''}
              onChange={(e) => onChange?.('identificacaoMaquina', e.target.value)}
              className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                       focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                       bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                       sm:text-sm"
              placeholder={t('apreciacaoEdicao.cabecalho.placeholderIdentificacao')}
            />
          ) : (
            <p className="text-sm text-gray-900 dark:text-white">{dados.identificacaoMaquina || '-'}</p>
          )}
        </div>

        {/* Local (Identificação Documental) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
            {t('comum.rotulo.local')}
          </label>
          {modoEdicao ? (
            <input
              type="text"
              value={dados.setorMaquina || ''}
              onChange={(e) => onChange?.('setorMaquina', e.target.value)}
              className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                       focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                       bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                       sm:text-sm"
              placeholder={t('apreciacaoEdicao.cabecalho.placeholderLocal')}
            />
          ) : (
            <p className="text-sm text-gray-900 dark:text-white">{dados.setorMaquina || '-'}</p>
          )}
        </div>
      </div>

      {/* Status de Liberação */}
      <div className="border-t dark:border-schmersal-gray-dark pt-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
          {t('apreciacaoEdicao.cabecalho.statusLiberacao')}
        </label>
        {modoEdicao ? (
          <select
            value={dados.statusLiberacao || 'nao_liberado'}
            onChange={(e) => onChange?.('statusLiberacao', e.target.value)}
            className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                     focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                     bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                     sm:text-sm"
          >
            <option value="nao_liberado">{t('apreciacaoEdicao.cabecalho.status.naoLiberado')}</option>
            <option value="com_ressalvas">{t('apreciacaoEdicao.cabecalho.status.comRessalvas')}</option>
            <option value="liberado">{t('apreciacaoEdicao.cabecalho.status.liberado')}</option>
          </select>
        ) : (
          <div
            className={`inline-flex px-3 py-1 rounded-full text-sm font-semibold border ${getStatusColor(
              dados.statusLiberacao
            )}`}
          >
            {getStatusLabel(dados.statusLiberacao)}
          </div>
        )}
      </div>
    </div>
  );
}

